<script lang="ts">
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { Button } from '$lib/components/ui/button';
	import * as Card from '$lib/components/ui/card';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import { Separator } from '$lib/components/ui/separator';
	import { Textarea } from '$lib/components/ui/textarea';
	import * as Alert from '$lib/components/ui/alert';
	import {
		Collections,
		type BookingsResponse,
		type ListingsResponse,
		type BoatsResponse,
		type UsersResponse,
		type PortsResponse,
		type ReviewsResponse
	} from '@/pocketbase/pocketbase-types';
	import { pb, currentUser } from '@/utils/ApiClient';
	import { Star } from '@lucide/svelte';
	import Calendar from '@lucide/svelte/icons/calendar';
	import ChevronLeft from '@lucide/svelte/icons/chevron-left';
	import MapPin from '@lucide/svelte/icons/map-pin';
	import Sailboat from '@lucide/svelte/icons/sailboat';
	import User from '@lucide/svelte/icons/user';
	import Users from '@lucide/svelte/icons/users';
	import Mail from '@lucide/svelte/icons/mail';
	import * as Avatar from '$lib/components/ui/avatar';

	type ExpandedBooking = BookingsResponse<{
		listing: ListingsResponse<
			string[],
			{
				boat: BoatsResponse<{ port: PortsResponse }>;
				user: UsersResponse;
			}
		>;
		user: UsersResponse;
	}>;

	// Obtener datos de load
	let booking: ExpandedBooking | null = $derived(page.data.booking);
	let existingReview: ReviewsResponse | null = $derived(page.data.existingReview);
	let role: 'guest' | 'owner' = $derived(page.data.role || 'guest');
	let error: string | null = $derived(page.data.error);
	let loading = $derived(page.data.booking === null && page.data.error === null);

	// Formulario
	let rating = $state<number>(0);
	let hoveredRating = $state<number>(0);
	let comment = $state('');
	let photos: File[] = $state([]);
	let submitting = $state(false);
	let photoError = $state<string | null>(null);
	const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB en bytes

	// Pre-llenar formulario si ya existe una reseña
	$effect(() => {
		if (existingReview) {
			rating = existingReview.rating;
			comment = existingReview.comment || '';
		}
	});

	function handlePhotoChange(event: Event) {
		const target = event.target as HTMLInputElement;
		photoError = null;

		if (target.files) {
			const files = Array.from(target.files);

			// Validar tamaño de cada archivo
			const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);
			if (oversizedFiles.length > 0) {
				photoError = `Algunas imágenes superan el límite de 5MB. Por favor selecciona imágenes más pequeñas.`;
				target.value = ''; // Limpiar el input
				return;
			}

			// Validar que sean imágenes
			const invalidFiles = files.filter((file) => !file.type.startsWith('image/'));
			if (invalidFiles.length > 0) {
				photoError = 'Por favor selecciona solo archivos de imagen.';
				target.value = '';
				return;
			}

			photos = files.slice(0, 10); // Máximo 10 fotos
		}
	}

	function removePhoto(index: number) {
		photos = photos.filter((_, i) => i !== index);
	}

	async function submitReview() {
		if (!booking || !$currentUser || rating === 0) {
			alert('Por favor selecciona una calificación');
			return;
		}

		submitting = true;

		try {
			const formData = new FormData();
			formData.append('reviewer', $currentUser.id);

			// Determinar el reviewee según el rol
			if (role === 'owner') {
				// Si es propietario, valora al usuario que hizo la reserva
				const bookingUserId = typeof booking.user === 'string' ? booking.user : booking.user[0];
				formData.append('reviewee', bookingUserId);
			} else {
				// Si es guest, valora al propietario del listing
				formData.append('reviewee', booking.expand?.listing?.expand?.user?.id || '');
			}

			// booking.listing puede ser un string o un array
			const listingId = typeof booking.listing === 'string' ? booking.listing : booking.listing[0];
			formData.append('listing', listingId);
			formData.append('booking', booking.id);
			formData.append('rating', rating.toString());
			if (comment) {
				formData.append('comment', comment);
			}

			// Agregar fotos si hay
			photos.forEach((photo) => {
				formData.append('photos', photo);
			});

			if (existingReview) {
				// Actualizar reseña existente
				await pb.collection(Collections.Reviews).update(existingReview.id, formData);
			} else {
				// Crear nueva reseña
				await pb.collection(Collections.Reviews).create(formData);
			}

			// Redirigir a la página de detalles de la reserva según el rol
			if (role === 'owner') {
				goto(`/cuenta/experiencias/reservas/reserva?id=${booking.id}`);
			} else {
				goto(`/cuenta/reservas/reserva?id=${booking.id}`);
			}
		} catch (err: any) {
			console.error('Error al guardar la reseña:', err);
			alert('Error al guardar la reseña. Por favor inténtalo de nuevo.');
		} finally {
			submitting = false;
		}
	}

	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString('es-ES', {
			day: 'numeric',
			month: 'long',
			year: 'numeric'
		});
	}

	function goBack() {
		if (booking) {
			if (role === 'owner') {
				goto(`/cuenta/experiencias/reservas/reserva?id=${booking.id}`);
			} else {
				goto(`/cuenta/reservas/reserva?id=${booking.id}`);
			}
		} else {
			if (role === 'owner') {
				goto('/cuenta/experiencias/reservas');
			} else {
				goto('/cuenta/experiencias/reservas');
			}
		}
	}

	// Obtener el avatar del propietario
	function getOwnerAvatar() {
		if (!booking) return null;
		const avatar = booking.expand?.listing?.expand?.user?.avatar;
		if (avatar && booking.expand?.listing?.expand?.user) {
			return pb.files.getURL(booking.expand.listing.expand.user, avatar, { thumb: '100x100' });
		}
		return null;
	}
</script>

<svelte:head>
	<title>Valorar Experiencia - Zarpar</title>
</svelte:head>

<div class="container mx-auto max-w-4xl px-4 py-8">
	<!-- Encabezado -->
	<div class="mb-6 flex items-center justify-between">
		<Button variant="ghost" onclick={goBack} class="gap-2">
			<ChevronLeft class="h-4 w-4" />
			Volver
		</Button>
		<h1 class="text-2xl font-bold">Valorar Experiencia</h1>
		<div></div>
	</div>

	{#if loading}
		<div class="flex items-center justify-center py-12">
			<p class="text-muted-foreground">Cargando...</p>
		</div>
	{:else if error}
		<Alert.Root variant="destructive">
			<Alert.Title>Error</Alert.Title>
			<Alert.Description>{error}</Alert.Description>
		</Alert.Root>
	{:else if booking}
		<div class="grid gap-6 lg:grid-cols-3">
			<!-- Columna principal con formulario -->
			<div class="lg:col-span-2">
				<Card.Root>
					<Card.Header>
						<Card.Title>Tu Valoración</Card.Title>
						<Card.Description>
							{#if role === 'owner'}
								Comparte tu opinión sobre el marinero que realizó la reserva
							{:else}
								Comparte tu experiencia para ayudar a otros usuarios
							{/if}
						</Card.Description>
					</Card.Header>
					<Card.Content class="space-y-6">
						<!-- Rating con estrellas -->
						<div class="space-y-2">
							<Label>Calificación *</Label>
							<div class="flex items-center gap-2">
								{#each Array(5) as _, i}
									<button
										type="button"
										onclick={() => {
											rating = i + 1;
										}}
										onmouseenter={() => {
											hoveredRating = i + 1;
										}}
										onmouseleave={() => {
											hoveredRating = 0;
										}}
										class="cursor-pointer transition-colors"
									>
										<Star
											class="h-8 w-8 transition-colors {i + 1 <= (hoveredRating || rating)
												? 'fill-yellow-400 text-yellow-400'
												: 'text-gray-300'}"
										/>
									</button>
								{/each}
								{#if rating > 0}
									<span class="text-muted-foreground ml-2 text-sm">{rating} de 5</span>
								{/if}
							</div>
						</div>

						<Separator />

						<!-- Comentario -->
						<div class="space-y-2">
							<Label for="comment">Comentario</Label>
							<Textarea
								id="comment"
								bind:value={comment}
								placeholder="Comparte tu experiencia..."
								rows={6}
								maxlength={500}
							/>
							<p class="text-muted-foreground text-xs">{comment.length}/500 caracteres</p>
						</div>

						<Separator />

						<!-- Fotos -->
						<div class="space-y-2">
							<Label for="photos">Fotos (opcional, máximo 10, 5MB por imagen)</Label>
							<Input
								id="photos"
								type="file"
								accept="image/*"
								multiple
								onchange={handlePhotoChange}
							/>
							{#if photoError}
								<p class="text-sm text-red-600">{photoError}</p>
							{/if}
							<p class="text-muted-foreground text-xs">
								Tamaño máximo: 5MB por imagen. Formatos aceptados: JPG, PNG, WEBP
							</p>
							{#if photos.length > 0}
								<div class="grid grid-cols-4 gap-2">
									{#each photos as photo, index}
										<div class="relative">
											<img
												src={URL.createObjectURL(photo)}
												alt="Preview"
												class="h-24 w-full rounded-lg object-cover"
											/>
											<button
												type="button"
												onclick={() => removePhoto(index)}
												class="absolute top-1 right-1 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
											>
												×
											</button>
											<div
												class="absolute bottom-1 left-1 rounded bg-black/50 px-1 text-xs text-white"
											>
												{(photo.size / 1024 / 1024).toFixed(2)}MB
											</div>
										</div>
									{/each}
								</div>
							{/if}
						</div>

						<Separator />

						<!-- Botón de enviar -->
						<Button onclick={submitReview} disabled={submitting || rating === 0} class="w-full">
							{#if submitting}
								Enviando...
							{:else if existingReview}
								Actualizar Valoración
							{:else}
								Enviar Valoración
							{/if}
						</Button>
					</Card.Content>
				</Card.Root>
			</div>

			<!-- Columna lateral con resumen -->
			<div class="lg:col-span-1">
				<div class="sticky top-6">
					<Card.Root>
						<Card.Header>
							<Card.Title>Resumen de la Reserva</Card.Title>
						</Card.Header>
						<Card.Content class="space-y-4">
							<!-- Información del barco -->
							{#if booking.expand?.listing?.expand?.boat}
								<div class="flex gap-3">
									<img
										src={pb.files.getURL(
											booking.expand.listing.expand.boat,
											booking.expand.listing.expand.boat.photos?.[0],
											{ thumb: '100x100' }
										)}
										alt="Foto del barco"
										class="h-20 w-20 rounded-lg object-cover"
									/>
									<div class="flex-1 space-y-1">
										<h3 class="font-semibold">
											{booking.expand.listing.title || 'Experiencia náutica'}
										</h3>
										<div class="text-muted-foreground flex items-center gap-1 text-sm">
											<Sailboat class="h-4 w-4" />
											{booking.expand.listing.expand.boat.name}
										</div>
									</div>
								</div>
							{/if}

							<Separator />

							<!-- Fechas -->
							<div class="space-y-2">
								<div class="flex items-center gap-2 text-sm">
									<Calendar class="h-4 w-4" />
									<span class="font-medium">Fechas</span>
								</div>
								<div class="text-muted-foreground text-sm">
									{formatDate(booking.startDate)} - {formatDate(booking.endDate)}
								</div>
							</div>

							<Separator />

							<!-- Ubicación -->
							{#if booking.expand?.listing?.expand?.boat?.expand?.port}
								<div class="space-y-2">
									<div class="flex items-center gap-2 text-sm">
										<MapPin class="h-4 w-4" />
										<span class="font-medium">Puerto</span>
									</div>
									<div class="text-muted-foreground text-sm">
										{booking.expand.listing.expand.boat.expand.port.name}
									</div>
								</div>
							{/if}

							<Separator />

							<!-- Pasajeros -->
							<div class="space-y-2">
								<div class="flex items-center gap-2 text-sm">
									<Users class="h-4 w-4" />
									<span class="font-medium">Pasajeros</span>
								</div>
								<div class="text-muted-foreground text-sm">
									{booking.passangers}
									{booking.passangers === 1 ? 'persona' : 'personas'}
								</div>
							</div>
						</Card.Content>
					</Card.Root>

					<!-- Card de la persona que está siendo valorada -->
					{#if role === 'owner'}
						<!-- Si es propietario, mostrar información del usuario que hizo la reserva -->
						{#if booking.expand?.user}
							<Card.Root class="mt-6">
								<Card.Header>
									<Card.Title>Información del Marinero</Card.Title>
								</Card.Header>
								<Card.Content>
									<div class="flex items-start gap-4">
										<Avatar.Root class="h-16 w-16">
											{#if booking.expand.user.avatar}
												<Avatar.Image
													src={pb.files.getURL(booking.expand.user, booking.expand.user.avatar, {
														thumb: '100x100'
													})}
													alt="Avatar del Marinero"
												/>
											{/if}
											<Avatar.Fallback>
												<User class="h-8 w-8" />
											</Avatar.Fallback>
										</Avatar.Root>

										<div class="flex-1 space-y-2">
											<h3 class="text-lg font-semibold">
												@{booking.expand.user.username || 'Usuario'}
											</h3>

											{#if booking.expand.user.bio}
												<p class="text-muted-foreground text-sm">
													{booking.expand.user.bio}
												</p>
											{/if}

											<div class="flex gap-4 text-sm">
												{#if booking.expand.user.email}
													<div class="flex items-center gap-1">
														<Mail class="h-3 w-3" />
														<span class="text-muted-foreground">
															{booking.expand.user.email}
														</span>
													</div>
												{/if}
											</div>
										</div>
									</div>
								</Card.Content>
							</Card.Root>
						{/if}
					{:else}
						<!-- Si es guest, mostrar información del propietario -->
						{#if booking.expand?.listing?.expand?.user}
							<Card.Root class="mt-6">
								<Card.Header>
									<Card.Title>Información del Armador</Card.Title>
								</Card.Header>
								<Card.Content>
									<div class="flex items-start gap-4">
										<Avatar.Root class="h-16 w-16">
											{#if getOwnerAvatar()}
												<Avatar.Image src={getOwnerAvatar()} alt="Avatar del Armador" />
											{/if}
											<Avatar.Fallback>
												<User class="h-8 w-8" />
											</Avatar.Fallback>
										</Avatar.Root>

										<div class="flex-1 space-y-2">
											<h3 class="text-lg font-semibold">
												{booking.expand.listing.expand.user.username || 'Usuario'}
											</h3>

											{#if booking.expand.listing.expand.user.bio}
												<p class="text-muted-foreground text-sm">
													{booking.expand.listing.expand.user.bio}
												</p>
											{/if}

											<div class="flex gap-4 text-sm">
												{#if booking.expand.listing.expand.user.email}
													<div class="flex items-center gap-1">
														<Mail class="h-3 w-3" />
														<span class="text-muted-foreground">
															{booking.expand.listing.expand.user.email}
														</span>
													</div>
												{/if}
											</div>
										</div>
									</div>
								</Card.Content>
							</Card.Root>
						{/if}
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>
